import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Shield, CheckCircle2, AlertCircle, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";

const StatusDot = ({ healthy }) => (
  <span className="relative inline-flex h-4 w-4">
    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${healthy ? "bg-[#8DB87A]" : "bg-[#EC008C]"}`}></span>
    <span className={`relative inline-flex rounded-full h-4 w-4 ${healthy ? "bg-[#8DB87A]" : "bg-[#EC008C]"}`}></span>
  </span>
);

const PublicStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/health-status`);
      setData(res.data);
    } catch (e) {
      setData({ status: "unknown", verdict: "unknown", message: "Status temporarily unavailable.", last_checked_at: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  const healthy = data?.verdict === "healthy";
  const lastCheck = data?.last_checked_at ? new Date(data.last_checked_at) : null;

  return (
    <div className="min-h-screen bg-[#050810] flex flex-col" data-testid="public-status-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/security">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white" data-testid="back-security-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Security &amp; Trust
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 mb-6">
              <Shield className="w-4 h-4 text-[#EC008C]" />
              <span className="text-sm text-[#EC008C] font-medium tracking-wide">PUBLIC STATUS</span>
            </div>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
              Platform Status
            </h1>
            <p className="text-[#8A92A0] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Live status of <span className="text-[#EC008C] font-semibold">stardomfans.com</span> — automatically verified every 24 hours by our in-house AI.
            </p>
          </div>

          {loading ? (
            <div className="card p-12 text-center" data-testid="status-loading">
              <div className="spinner mx-auto" />
              <p className="text-[#8A92A0] mt-4 text-sm">Loading status…</p>
            </div>
          ) : (
            <div
              className={`card p-10 sm:p-12 text-center bg-gradient-to-br ${healthy
                ? "from-[#8DB87A]/10 via-transparent to-[#8DB87A]/5 border-[#8DB87A]/30"
                : "from-[#EC008C]/10 via-transparent to-[#EC008C]/5 border-[#EC008C]/30"
              }`}
              data-testid="status-card"
            >
              <div className="flex items-center justify-center gap-4 mb-6">
                <StatusDot healthy={healthy} />
                <h2 className={`font-['Unbounded'] text-3xl sm:text-4xl font-bold ${healthy ? "text-[#8DB87A]" : "text-[#EC008C]"}`} data-testid="status-verdict">
                  {healthy ? "All Systems Operational" : "Issues Under Investigation"}
                </h2>
              </div>
              <p className="text-[#8A92A0] text-base sm:text-lg leading-relaxed mb-8 max-w-xl mx-auto" data-testid="status-message">
                {data?.message}
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-[#6A7280]">
                {healthy ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span>
                  Last checked:{" "}
                  {lastCheck
                    ? `${lastCheck.toLocaleDateString()} ${lastCheck.toLocaleTimeString()}`
                    : "Never (awaiting first run)"}
                </span>
              </div>
              <Button
                onClick={fetchStatus}
                variant="ghost"
                className="mt-6 text-[#8A92A0] hover:text-white text-sm"
                data-testid="refresh-status-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          )}

          <p className="text-xs text-[#6A7280] text-center mt-8">
            This status reflects automated platform checks. For incident history or detailed reports,
            please contact your StardomFans.com account manager.
          </p>
        </div>
      </main>

      <footer className="py-8 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/copyright" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Copyright</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/status" className="text-[#EC008C] text-sm">Status</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicStatus;
